import React from 'react';
import NoData from 'components/_common/NoData';
import Loading from 'components/_common/Loading';
import SearchFilter from 'components/_common/SearchFilter';
import PageUtil from 'utils/Page.util';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { DateType, ReceptionItemKind, getReceptionStatus, getReceptionType, getPaymentMethod, getFlowType, ReceptionType, PaymentMethod } from 'enums/reception.enum';

const RepairV = ({
    params,
    setParams,
    handleChange,
    onSearch,
    setSearch,
    onClearFilter,
    onToggleDatePickerMode,
    onHandleSearchDate,
    onOpenCreate,
    onOpenEdit,
    onChangeStatus,
    onTodaySearch,
    handleExportExcel
}) => {
    const { list, isLoaded, error, search, kpiData, kpiLoaded } = params;

    // 날짜 필드 옵션
    const dateTypeOptions = [
        { value: DateType.RECEIVED, label: '접수일자' },
        { value: DateType.REPAIR, label: '수리일자' }
    ];

    // 검색 매개변수 변경 핸들러
    const handleSearchChange = (field, value) => {
        const cur = PageUtil.deepCopy(params);
        cur.search[field] = value;
        setParams(cur);
    };

    const getTotalSummary = (row) => {
        const allOptions = [];

        row.items.forEach(item => {
            allOptions.push(...item.options || []);
        });

        // productCode별로 quantity 합치기
        const mergedOptions = allOptions.reduce((acc, opt) => {
            if (opt.kind != ReceptionItemKind.DISCOUNT) {
                const existing = acc.find(item => item.productName === opt.productName);
                if (existing) {
                    existing.quantity += opt.quantity;
                } else {
                    acc.push({ ...opt });
                }
            }
            return acc;
        }, []);

        const allSummaries = mergedOptions
            .filter(opt => opt.quantity > 0)
            .map(opt => opt.quantity === 1 ? opt.productName : `${opt.productName}×${opt.quantity}`)
            .join(', ');

        return allSummaries;
    }

    // KPI 스켈레톤 로딩 컴포넌트
    const KPISkeleton = () => (
        <div className="mb-4">
            <div className="row g-3 mb-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div style={{ width: '60%' }}>
                                        <div className="placeholder-glow">
                                            <span className="placeholder col-8"></span>
                                        </div>
                                        <div className="placeholder-glow mt-2">
                                            <span className="placeholder col-6"></span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="placeholder-glow">
                                            <span className="placeholder col-12" style={{ width: '40px', height: '40px' }}></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <>
            {isLoaded && error == null ? (
                <>
                    <div className="content-header">
                        <h1>
                            <span>수리관리</span>
                        </h1>
                    </div>

                    <div className="container-fluid">
                        {/* ===== KPI 섹션 시작 ===== */}
                        {!kpiLoaded ? (
                            <KPISkeleton />
                        ) : (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="card card-outline card-success">
                                        <div className="card-header">
                                            <h3 className="card-title">
                                                <i className="fas fa-wrench mr-2"></i>
                                                수리관리 현황
                                            </h3>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                {/* 1. 총 수리한 보일러대수(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                총 수리한 보일러대수(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.totalRepairCount?.toLocaleString() ?? '0'}건
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 2. 1년 6개월이내 수리(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                1년 6개월이내 수리(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.recentRepairCount?.toLocaleString() ?? '0'}건
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 3. 공임비 청구 보일러(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                공임비 청구 보일러(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.laborChargeCount?.toLocaleString() ?? '0'}건
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 4. 기판 물통 수리(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                기판 물통 수리(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.boardWaterTankCount?.toLocaleString() ?? '0'}건
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 5. 기판 물통 수리 외(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                기판 물통 수리 외(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.otherRepairCount?.toLocaleString() ?? '0'}건
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 6. 공임비용(월) */}
                                                <div className="col-lg-2 col-md-4 col-sm-6">
                                                    <div className="info-box" style={{ backgroundColor: '#FFFF00', minHeight: '90px' }}>
                                                        <div className="info-box-content" style={{ padding: '10px' }}>
                                                            <span className="info-box-text" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                                공임비용(월)
                                                            </span>
                                                            <span className="info-box-number" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {kpiData.laborChargeCost?.toLocaleString() ?? '0'}원
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* ===== KPI 섹션 끝 ===== */}

                        {/* 고급 검색 필터 */}
                        <SearchFilter
                            searchParams={search}
                            onSearchChange={handleSearchChange}
                            onSearch={onSearch}
                            onClearFilter={onClearFilter}
                            todayButtonName={"수리 Today"}
                            onTodaySearch={onTodaySearch}
                            showStatusFilter={false}
                            showTypeFilter={true}
                            showDateRange={true}
                            showDateType={true}
                            dateTypeOptions={dateTypeOptions}
                            placeholder="고객명, 연락처, 지역 등 검색"
                            onExcelDownload={handleExportExcel}
                        // additionalFilters={
                        //     <div className="row g-2">
                        //         <div className="col-4" style={{ display: 'flex', alignItems: 'center' }}>
                        //             <label className="form-label text-muted small">기간 프리셋</label>
                        //             <div className='form-control form-control-sm'>
                        //                 <select
                        //                     className="form-select form-select-sm"
                        //                     onChange={(e) => {
                        //                         const value = e.target.value;
                        //                         const today = PageUtil.getCurrentDate();
                        //                         let startDate = '';

                        //                         if (value === 'today') {
                        //                             startDate = today;
                        //                         } else if (value === 'month') {
                        //                             startDate = PageUtil.getFirstDayOfMonth();
                        //                         } else if (value === 'all') {
                        //                             startDate = '';
                        //                         }

                        //                         handleSearchChange('startDate', startDate);
                        //                         handleSearchChange('endDate', value === 'all' ? '' : today);
                        //                         onSearch();
                        //                     }}
                        //                     style={{ width: '100%' }}
                        //                 >
                        //                     <option value="">기간 선택</option>
                        //                     <option value="today">오늘</option>
                        //                     <option value="month">이번달</option>
                        //                     <option value="all">전체기간</option>
                        //                 </select>
                        //             </div>
                        //         </div>
                        //         <div className="col-4" style={{ display: 'flex', alignItems: 'center' }}>
                        //             <label className="form-label text-muted small">결제내역</label>
                        //             <div className='form-control form-control-sm'>
                        //                 <select
                        //                     className="form-select form-select-sm"
                        //                     value={search.paymentMethod || 'all'}
                        //                     onChange={(e) => handleSearchChange('paymentMethod', e.target.value)}
                        //                     style={{ width: '100%' }}
                        //                 >
                        //                     <option value="all">전체</option>
                        //                     {getPaymentMethod().map(method => (
                        //                         <option key={method.value} value={method.value}>
                        //                             {method.label}
                        //                         </option>
                        //                     ))}
                        //                 </select>
                        //             </div>
                        //         </div>
                        //     </div>
                        // }
                        />

                        {/* 목록 */}
                        <div className="card card-outline card-dark">
                            <div className="card-header">
                                <div className="card-title">수리 목록</div>
                                <div className="card-tools">
                                    <span className="badge bg-secondary">
                                        {PageUtil.formatToKRW(params.total)} 건
                                    </span>
                                </div>
                            </div>
                            <div className="card-body table-responsive p-0">
                                {list.length === 0 ? (
                                    <NoData />
                                ) : (
                                    <table className="table text-nowrap">
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 120 }}>처리상황</th>
                                                <th>처리유형</th>
                                                <th>입고일자</th>
                                                <th>수리일자</th>
                                                <th>결제내역</th>
                                                <th>연락처</th>
                                                <th>고객명</th>
                                                <th>보일러명</th>
                                                <th>제조년월</th>
                                                <th>총수리/판매내역</th>
                                                <th>입고/택발</th>
                                                <th>견적서확인</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {list.map((row) => (
                                                <tr key={row.id} style={row.blacklist ? { backgroundColor: '#ffe6e6' } : {}}>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={row.status || ''}
                                                            onChange={(e) => onChangeStatus(row, e.target.value)}
                                                            style={{
                                                                color: row.status === 'ARREARS' ? '#dc3545' : '',
                                                                fontWeight: row.status === 'ARREARS' ? 'bold' : '',
                                                                fontSize: row.status === 'ARREARS' ? '15px' : '',
                                                            }}
                                                        >
                                                            {params.statusSet.map((s) => (
                                                                <option key={s.value} value={s.value} style={{ textAlign: 'center' }}>
                                                                    {s.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={"badge " + (row.type == ReceptionType.NEW_EXCHANGE || row.type == ReceptionType.EXCHANGE ? ' bg-danger' : 'bg-info')}
                                                            style={{ fontSize: '15px', padding: '5px 10px' }} // 글자 크기와 여백 추가
                                                        >
                                                            {getReceptionType().find((t) => t.value === row.type)?.label || row.type}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '15px', fontWeight: 'bold' }}>
                                                        {row?.items
                                                            ?.filter(p => p.courierInboundDate)
                                                            .map(p => p.courierInboundDate)
                                                            .sort()[0] || ''}
                                                    </td>
                                                    <td style={{ fontSize: '15px', fontWeight: 'bold' }}>
                                                        {row.items
                                                            ?.filter(p => p.repairDate)
                                                            .map(p => p.repairDate)
                                                            .sort()[0] || ''}
                                                    </td>
                                                    <td>
                                                        <span className={"badge " + (row.paymentMethod == PaymentMethod.ARREARS ? ' bg-danger' : 'bg-secondary')}>
                                                            {getPaymentMethod().find((p) => p.value === row.paymentMethod)?.label || row.paymentMethod}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <a onClick={() => onOpenEdit(row)} style={{ cursor: 'pointer' }}>
                                                            {row.phone1 || ''}
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <strong>{row.customerName || ''}</strong>
                                                    </td>
                                                    <td>
                                                        <code>{(row.items?.find(p => p.kind === ReceptionItemKind.BOILER)?.modelName || '') + (row.items?.filter(p => p.kind === ReceptionItemKind.BOILER)?.length > 1 ? ' 外' : '')}</code>
                                                    </td>
                                                    <td>
                                                        <small>{row?.items?.find(p => p.manufactureMonth)?.manufactureMonth || ''}</small>
                                                    </td>
                                                    <td>
                                                        {getTotalSummary(row) || ''}
                                                    </td>
                                                    <td>
                                                        {getFlowType().find((f) => f.value === row.items?.find(p => p.kind === ReceptionItemKind.BOILER)?.flow)?.label || row.items?.find(p => p.kind === ReceptionItemKind.BOILER)?.flow || ''}
                                                    </td>
                                                    <td>
                                                        <small className="badge bg-light text-dark">
                                                            {row.estimateIssueDate ? 'Y' : 'N'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* 페이징 */}
                            {list.length > 0 && (
                                <PaginationControl
                                    page={params.search.page}
                                    total={params.total}
                                    limit={params.search.rows}
                                    changePage={(page) => {
                                        const cur = PageUtil.deepCopy(params);
                                        cur.search.page = page;
                                        setParams(cur);
                                        setSearch(true);
                                    }}
                                    ellipsis={1}
                                />
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <Loading />
            )}
        </>
    );
};

export default RepairV;